import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useOnHand, type LastCost, type SkuRow } from '@/hooks/inventory';
import type { SearchSelectHandle } from './SearchSelect';
import { ItemPicker, ItemPickerHead, type ItemSearch } from './ItemPicker';
import { formatNumber, roundMoney, roundTo } from '@/format';
import { money, quantity, rate, shortDate } from './ui';
import { useLineRemoval } from './lineRows';
import { BinPicker, type PickableBin } from './BinSelect';
import { DeliveryStrip, VEHICLE_LIST_ID, type ApplyOffer, type DeliveryOptions } from './DeliveryStrip';
import {
  applyBelow,
  fieldValue,
  offeredVehicles,
  linesBelowWith,
  newSupplierName,
  supplierGroups,
  type DeliveryField,
} from './receiptDelivery';
import { useVehicleNumbers } from './VehicleField';
import { PackedQtyInput } from './PackedQtyInput';
import { costPerEntryUnit, costPerItemUnit, costUnitOf, packedSuffix, UNIT_KEY, type EntryUnit } from '@/packing';

export interface ReceiptLine {
  /** Stable React key; never sent to the server. */
  key: string;
  skuId: string;
  /** The chosen item's label, kept so it still shows after search results move on. */
  skuLabel: string;
  qty: string;
  unitCost: string;
  batchNo: string;
  expiryDate: string;
  /** The line's storage bin; '' is loose in the store. */
  binId: string;
  /** The chosen bin's "code — name", kept so a bin deleted since it was
   *  chosen still shows by name instead of as a blank box. */
  binLabel: string;
  /** What the cost box was last prefilled for (see `costKey`). A box is
   *  prefilled once per item, so clearing it leaves it clear instead of the
   *  last price coming straight back. */
  costFilledFor: string;
  /** The cost in the box is the prefilled last price, not one anybody typed —
   *  so choosing another item may replace it. */
  costAuto: boolean;
  /** The saved line this one is, when editing; '' for a new one. */
  lineId: string;
  /** Who supplied this line: a supplier id, or `new:<name>` for one to be
   *  registered on save (see receiptDelivery). */
  supplierId: string;
  supplierLabel: string;
  /** The store this line is received into. */
  storeId: string;
  invoiceRef: string;
  vehicleNo: string;
  remarks: string;
  /** Its delivery details have been filled from the line above (or were
   *  saved with it), so they are never filled again — a box the receiver
   *  then empties stays empty. */
  carried: boolean;
}

let lineCounter = 0;

export function emptyReceiptLine(patch: Partial<ReceiptLine> = {}): ReceiptLine {
  lineCounter += 1;
  return {
    key: `line-${lineCounter}`,
    skuId: '',
    skuLabel: '',
    qty: '',
    unitCost: '',
    batchNo: '',
    expiryDate: '',
    binId: '',
    binLabel: '',
    costFilledFor: '',
    costAuto: false,
    lineId: '',
    supplierId: '',
    supplierLabel: '',
    storeId: '',
    invoiceRef: '',
    vehicleNo: '',
    remarks: '',
    carried: false,
    ...patch,
  };
}

/** What a line's last-price prefill depends on: the item, and who sells it. */
function costKey(line: Pick<ReceiptLine, 'skuId' | 'supplierId'>): string {
  return `${line.skuId}|${line.supplierId}`;
}

/**
 * The price to suggest for a line: what its own supplier charged last, else
 * what anybody did. `undefined` while nothing can be said — the costs are
 * still loading, or were stripped for a caller who may not see prices.
 */
function suggestedCost(
  info: LastCost | undefined,
  line: ReceiptLine,
): { cost: string | null; at: string | null; from: 'supplier' | 'other' | 'any' } | undefined {
  if (!info || info.lastCost === undefined) return undefined;
  const own = line.supplierId ? info.bySupplier?.find((s) => s.supplierId === line.supplierId) : undefined;
  if (own?.lastCost) return { cost: own.lastCost, at: own.lastCostAt ?? null, from: 'supplier' };
  // Asked about this supplier and they never supplied it: say the price
  // shown is somebody else's.
  return { cost: info.lastCost, at: info.lastCostAt ?? null, from: own ? 'other' : 'any' };
}

/** "60.0000" → "60", "4850.50" → "4850.5": the cost as a person would type it. */
export function plainCost(cost: string): string {
  return cost.includes('.') ? cost.replace(/\.?0+$/, '') : cost;
}

/** Nothing typed or chosen on the line yet. */
function isBlankReceipt(l: ReceiptLine): boolean {
  return [l.skuId, l.qty, l.unitCost, l.batchNo, l.expiryDate, l.binId].every((v) => v.trim() === '');
}

/**
 * Quantity × cost, to the paisa — the same rounding the server applies when it
 * values the line, so 2 × 10.2 reads Rs 20.40 here and on the posted receipt.
 */
export function lineTotal(line: Pick<ReceiptLine, 'qty' | 'unitCost'>): number | null {
  if (line.qty.trim() === '' || line.unitCost.trim() === '') return null;
  const value = Number(line.qty) * Number(line.unitCost);
  return Number.isFinite(value) ? roundMoney(value) : null;
}

/**
 * Quantities and costs are kept to 4 decimal places. When more were typed,
 * says what will be saved, so the receipt does not silently change later.
 */
function keptAs(text: string): string | null {
  const fraction = /\.(\d+)$/.exec(text.trim())?.[1];
  if (!fraction || fraction.length <= 4) return null;
  return formatNumber(roundTo(Number(text), 4), 4);
}

/**
 * The read-only line beside the cost box: what was paid last time, so a
 * price that has moved catches the eye instead of being typed over blind.
 *
 * `lastCost` can be `undefined` — stripped from the response for a caller
 * without inv.price.read, meaning there is nothing to say — which must read
 * differently from `null`, meaning the item genuinely has no purchase
 * history yet.
 */
function lastCost(
  costs: ReadonlyMap<string, LastCost>,
  line: ReceiptLine,
  /** Omitted on read-only lines, which have no cost box to fill. */
  onUse?: (cost: string) => void,
  /** The package the cost box is in, so the last price reads in it too. */
  per?: EntryUnit,
) {
  const suggested = suggestedCost(costs.get(line.skuId), line);
  if (!suggested) return null;
  if (suggested.cost === null) {
    return <span className="itemlines__hint">New item — no previous cost</span>;
  }
  const last = suggested.cost;
  const who =
    suggested.from === 'supplier'
      ? `Last from ${line.supplierLabel || 'this supplier'}`
      : suggested.from === 'other'
        ? 'Last (another supplier)'
        : 'Last';
  // Offered back only once the box is cleared: one click restores it, where
  // the old behaviour forced it back in while the receiver was deleting it.
  const offer = onUse !== undefined && line.unitCost.trim() === '';
  return (
    <span className="itemlines__hint">
      {who}: {per ? `${rate(costPerEntryUnit(last, per) ?? last)} per ${per.label}` : rate(last)}
      {suggested.at && <> · {shortDate(suggested.at)}</>}
      {offer && (
        <>
          {' '}
          <button type="button" className="linkbutton" onClick={() => onUse(plainCost(last))} title="Put the last price back in the box">
            Use
          </button>
        </>
      )}
    </span>
  );
}

/**
 * The hint under a line's Quantity box: how much of the item the receiving
 * store already holds, so a receiver can see what the delivery tops up.
 * Summed across lots — the lot being received is usually a new one. Until a
 * store is chosen, or while the balance loads, only the unit shows.
 */
function OnHandHint({ storeId, sku, qty }: { storeId: string; sku: SkuRow; qty: string }) {
  const { data: onHand } = useOnHand(storeId, sku.id);
  if (!storeId || onHand === undefined) return <span className="itemlines__hint">{sku.uom}</span>;
  // Shown only while the typed quantity is a valid number above zero. A
  // receipt adds to the store, so this is what it will hold once posted.
  const entered = Number(qty);
  const showAfter = qty.trim() !== '' && Number.isFinite(entered) && entered > 0;
  return (
    <>
      <span className="itemlines__hint">
        Available: {quantity(String(onHand), sku.uomPrecision)} {sku.uom}
        {packedSuffix(onHand, sku)}
      </span>
      {showAfter && (
        <span className="itemlines__hint">
          After receipt: {quantity(String(Number(onHand) + entered), sku.uomPrecision)} {sku.uom}
          {packedSuffix(Number(onHand) + entered, sku)}
        </span>
      )}
    </>
  );
}

/**
 * The line items of a receipt, as one aligned grid.
 *
 * Every row has exactly the same cells. Lot and Expiry columns appear for the
 * whole form as soon as any line's item needs them, and lines that do not
 * need them show a dash — so adding a batch-tracked item never reflows the
 * other lines, which is what made the old per-line grids look ragged. The Bin
 * column appears only when the store has bins (or a line still names one).
 */
export function ItemLines({
  lines,
  onChange,
  skus,
  search,
  showCost,
  locked = false,
  lastCosts,
  storeId,
  bins,
  minRows = 1,
  delivery,
  totalsBelow = true,
}: {
  lines: ReceiptLine[];
  onChange: (lines: ReceiptLine[]) => void;
  /** Every SKU seen so far, keyed by id, for units and tracking flags. */
  skus: ReadonlyMap<string, SkuRow>;
  search: ItemSearch;
  /** Cost and value columns. The cost is what the receiver types, so it is
   *  only hidden where the viewer could not see the stored cost either. */
  showCost: boolean;
  /** Lines shown but not editable. */
  locked?: boolean;
  /** Each item's last purchase cost, keyed by SKU id, for a read-only
   *  reference beside the cost box. Omit for a line type with no purchase
   *  cost of its own (issues, transfers, adjustments). */
  lastCosts?: ReadonlyMap<string, LastCost>;
  /** The receiving store. When given (even as '' before one is chosen), each
   *  line shows how much of its item that store holds under the Quantity box. */
  storeId?: string;
  /** The receiving store's active storage bins, undefined while they load. A
   *  Bin column appears only when there are any, so a store without bins
   *  looks exactly as it always has. */
  bins?: ReadonlyArray<PickableBin> | undefined;
  /** Rows the grid never drops below: removing a line under it puts a blank
   *  one back at the bottom. */
  minRows?: number;
  /** Stock-in: every line names its own supplier, store, bin, vehicle, invoice
   *  and remarks, in a second row under it. Its bins come from here,
   *  per store, instead of from `bins`. */
  delivery?: DeliveryOptions | undefined;
  /** Totals under the lines. A page that pins them beside its Save button
   *  (see `ItemTotals`) turns them off here. */
  totalsBelow?: boolean;
}) {
  // The line just added gets focus, so the next item can be typed straight away.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const needsLot = lines.some((l) => skus.get(l.skuId)?.isBatchTracked);
  const needsExpiry = lines.some((l) => skus.get(l.skuId)?.hasExpiry);
  // A line can still name a bin the store has since deleted (an old draft, a
  // receipt being edited); its column stays so that bin can be changed.
  const showBin = !delivery && ((bins?.length ?? 0) > 0 || lines.some((l) => l.binId));
  // Every strip keeps the same cells: a Bin box once any line's store has bins.
  const deliveryBins =
    delivery !== undefined &&
    (lines.some((l) => l.binId) || lines.some((l) => (delivery.binsByStore.get(l.storeId)?.length ?? 0) > 0));
  const vehicles = useVehicleNumbers();
  const [offer, setOffer] = useState<ApplyOffer | null>(null);
  // The unit each line's quantity is typed in, and a cost typed per package
  // as typed, keyed by line. The line itself only ever holds the quantity
  // and cost per one of the item's unit, which is what is saved.
  const [lineUnits, setLineUnits] = useState<ReadonlyMap<string, EntryUnit>>(() => new Map());
  const [costTyped, setCostTyped] = useState<ReadonlyMap<string, string>>(() => new Map());
  const withEntry = <T,>(map: ReadonlyMap<string, T>, key: string, value: T | undefined) => {
    const next = new Map(map);
    if (value === undefined) next.delete(key);
    else next.set(key, value);
    return next;
  };

  // Fills a blank cost box from the item's last purchase cost as soon as it
  // arrives. Choosing the item happens before the lookup resolves, so this
  // cannot run inside that selection's own onChange — it has to catch up
  // once `lastCosts` has the answer.
  //
  // Once per item: the line remembers what it was filled for, so a receiver
  // who backspaces the price away is left with an empty box, not the price
  // put straight back after every keystroke. A typed cost is never touched.
  useEffect(() => {
    if (!lastCosts || locked) return;
    const patched = lines.map((line) => {
      if (!line.skuId || line.costFilledFor === costKey(line)) return line;
      if (line.unitCost !== '' && !line.costAuto) return line;
      const suggested = suggestedCost(lastCosts.get(line.skuId), line);
      // Not loaded yet, or stripped for this caller: try again when it is.
      if (!suggested) return line;
      return suggested.cost
        ? { ...line, unitCost: plainCost(suggested.cost), costAuto: true, costFilledFor: costKey(line) }
        : { ...line, costFilledFor: costKey(line) };
    });
    if (patched.some((line, i) => line !== lines[i])) onChange(patched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCosts, lines, locked]);

  const columns = [
    'minmax(19rem, 4fr)',
    ...(needsLot ? ['minmax(8rem, 1.3fr)'] : []),
    ...(needsExpiry ? ['minmax(9rem, 1.2fr)'] : []),
    ...(showBin ? ['minmax(9rem, 1.3fr)'] : []),
    'minmax(7rem, 1fr)',
    ...(showCost ? ['minmax(7rem, 1fr)', 'minmax(7rem, 1fr)'] : []),
    ...(locked ? [] : ['var(--target-size)']),
  ].join(' ');
  const gridStyle = { '--itemlines-cols': columns } as CSSProperties;

  function update(key: string, patch: Partial<ReceiptLine>) {
    setOffer(null);
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  /**
   * A change to a line's delivery details. Once it is made (a pick, or
   * leaving a text box), the lines straight below still showing the old
   * value — the ones that copied it — are offered the new one.
   */
  function changeDelivery(
    index: number,
    patch: Partial<ReceiptLine>,
    field: DeliveryField,
    oldValue: string,
    offerNow: boolean,
  ) {
    const line = lines[index]!;
    const next = Object.keys(patch).length
      ? lines.map((l) => (l.key === line.key ? { ...l, ...patch } : l))
      : lines;
    if (next !== lines) onChange(next);
    const changed = fieldValue(next[index]!, field) !== oldValue;
    const keys = offerNow && changed ? linesBelowWith(next, index, field, oldValue) : [];
    setOffer(keys.length ? { key: line.key, field, keys } : null);
  }

  function applyOffer() {
    if (!offer) return;
    const index = lines.findIndex((l) => l.key === offer.key);
    if (index >= 0) onChange(applyBelow(lines, index, offer.field, offer.keys));
    setOffer(null);
  }

  const { canRemove, remove, buttonRef } = useLineRemoval(lines, onChange, emptyReceiptLine, isBlankReceipt, minRows);

  /** Each line's Item picker, so choosing one can move focus straight to its
   *  Quantity box — the "type, Enter, type, Enter" rhythm a paper invoice
   *  needs, with no mouse in between. */
  const itemRefs = useRef(new Map<string, SearchSelectHandle | null>());
  const qtyRefs = useRef(new Map<string, HTMLInputElement | null>());

  function addLine() {
    const added = emptyReceiptLine();
    setFocusKey(added.key);
    onChange([...lines, added]);
  }

  /**
   * Enter on a line's Quantity or Cost box moves to the next line's Item box,
   * and on the last line adds a fresh one and focuses that — the same result
   * as clicking "+ Add item", without leaving the keyboard. A form that opens
   * with blank rows ready is typed down rather than added to, so Enter must
   * walk them instead of dead-ending on the first. A last line still missing
   * its item or a quantity above zero shows the browser's own validation
   * instead of silently doing nothing, since a receiver mid-invoice needs to
   * know why Enter did not move on.
   */
  function onLineKeyDown(e: ReactKeyboardEvent<HTMLInputElement>, line: ReceiptLine, index: number) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = lines[index + 1];
    if (next) {
      itemRefs.current.get(next.key)?.focus();
      return;
    }
    if (!line.skuId || !(Number(line.qty) > 0)) {
      e.currentTarget.reportValidity();
      return;
    }
    addLine();
  }


  return (
    <div className="itemlines">
      <div className="itemlines__row itemlines__head" style={gridStyle} aria-hidden="true">
        <ItemPickerHead />
        {needsLot && <span>Lot</span>}
        {needsExpiry && <span>Expiry</span>}
        {showBin && <span>Bin</span>}
        <span>Quantity *</span>
        {showCost && <span>Cost per unit</span>}
        {showCost && <span className="num">Line total</span>}
        {!locked && <span />}
      </div>

      {lines.map((line, index) => {
        const sku = skus.get(line.skuId);
        const total = lineTotal(line);
        return (
          <div key={line.key} className="itemlines__row itemlines__line" style={gridStyle}>
            <div className="itemlines__cell itemlines__cell--item">
              <ItemPicker
                ref={(handle) => {
                  itemRefs.current.set(line.key, handle);
                }}
                index={index}
                disabled={locked}
                value={line.skuId}
                selectedLabel={line.skuLabel}
                search={search}
                autoFocus={focusKey === line.key}
                onChange={(option) =>
                  update(line.key, {
                    skuId: option?.value ?? '',
                    skuLabel: option?.label ?? '',
                    batchNo: '',
                    expiryDate: '',
                    // The last item's price is no use for this one; a price
                    // somebody typed stays.
                    ...(line.costAuto ? { unitCost: '', costAuto: false } : {}),
                  })
                }
                onPick={() => qtyRefs.current.get(line.key)?.focus()}
              />
            </div>

            {needsLot && (
              <div className="itemlines__cell">
                {sku?.isBatchTracked ? (
                  <label className="field">
                    <span className="itemlines__label">Lot<span className="sr-only"> for item {index + 1}</span></span>
                    <input
                      className="field__input field__input--mono"
                      value={line.batchNo}
                      disabled={locked}
                      onChange={(e) => update(line.key, { batchNo: e.target.value })}
                      placeholder="Supplier lot no."
                      title="Optional — left blank, the lot is named after this receipt"
                    />
                  </label>
                ) : (
                  <span className="itemlines__na" title="This item is not tracked by lot">
                    —
                  </span>
                )}
              </div>
            )}

            {needsExpiry && (
              <div className="itemlines__cell">
                {sku?.hasExpiry ? (
                  <label className="field">
                    <span className="itemlines__label">Expiry<span className="sr-only"> for item {index + 1}</span></span>
                    <input
                      className="field__input"
                      type="date"
                      required
                      value={line.expiryDate}
                      disabled={locked}
                      onChange={(e) => update(line.key, { expiryDate: e.target.value })}
                    />
                  </label>
                ) : (
                  <span className="itemlines__na" title="This item has no expiry date">
                    —
                  </span>
                )}
              </div>
            )}

            {showBin && (
              <div className="itemlines__cell">
                <BinPicker
                  bins={bins ?? []}
                  label={`Bin for item ${index + 1}`}
                  visibleLabel="Bin"
                  labelClassName="itemlines__label"
                  placeholder="Loose"
                  disabled={locked}
                  value={line.binId}
                  selectedLabel={line.binLabel || undefined}
                  onChange={(binId) => {
                    const bin = bins?.find((b) => b.id === binId);
                    update(line.key, { binId, binLabel: bin ? `${bin.code} — ${bin.nameEn}` : '' });
                  }}
                />
                {line.binId && bins && !bins.some((b) => b.id === line.binId) && (
                  <span className="itemlines__hint itemlines__hint--warn">Deleted — choose another bin</span>
                )}
              </div>
            )}

            <div className="itemlines__cell">
              <PackedQtyInput
                label="Quantity"
                srLabel={` for item ${index + 1}`}
                labelClassName="itemlines__label"
                item={sku}
                itemKey={line.skuId}
                startWith="package"
                required
                disabled={locked}
                value={line.qty}
                onChange={(qty) => update(line.key, { qty })}
                onUnitChange={(unit) => {
                  setLineUnits((m) => withEntry(m, line.key, unit));
                  setCostTyped((m) => withEntry(m, line.key, undefined));
                }}
                inputRef={(el) => {
                  qtyRefs.current.set(line.key, el);
                }}
                onKeyDown={(e) => onLineKeyDown(e, line, index)}
              />
              {sku &&
                (storeId !== undefined || delivery ? (
                  <OnHandHint storeId={delivery ? line.storeId : storeId!} sku={sku} qty={line.qty} />
                ) : (
                  <span className="itemlines__hint">{sku.uom}</span>
                ))}
              {keptAs(line.qty) && <span className="itemlines__hint">Will be saved as {keptAs(line.qty)}</span>}
            </div>

            {showCost && (() => {
              // A quantity typed in Cartons is priced per Carton: the box
              // shows and takes the price in it, and the line keeps it per
              // one of the item's unit.
              const typedIn = sku ? lineUnits.get(line.key) : undefined;
              const per = typedIn && typedIn.key !== UNIT_KEY ? costUnitOf(typedIn) : undefined;
              const typedCost = costTyped.get(line.key);
              const shownCost = !per
                ? line.unitCost
                : typedCost !== undefined && costPerItemUnit(typedCost, per) === line.unitCost
                  ? typedCost
                  : line.unitCost === ''
                    ? ''
                    : (costPerEntryUnit(line.unitCost, per) ?? line.unitCost);
              return (
                <div className="itemlines__cell">
                  <label className="field">
                    <span className="itemlines__label">Cost per unit<span className="sr-only"> for item {index + 1}</span></span>
                    <input
                      className="field__input num"
                      type="number"
                      step="any"
                      min="0"
                      inputMode="decimal"
                      disabled={locked}
                      value={shownCost}
                      onChange={(e) => {
                        const typed = e.target.value;
                        if (!per) {
                          update(line.key, { unitCost: typed, costAuto: false });
                          return;
                        }
                        setCostTyped((m) => withEntry(m, line.key, typed));
                        update(line.key, {
                          unitCost: typed.trim() === '' ? '' : (costPerItemUnit(typed, per) ?? typed),
                          costAuto: false,
                        });
                      }}
                      onKeyDown={(e) => onLineKeyDown(e, line, index)}
                    />
                  </label>
                  {sku && (
                    <span className="itemlines__hint">
                      per {per ? per.label : sku.uom}
                      {per && line.unitCost !== '' && ` · ${rate(line.unitCost)} per ${sku.uom}`}
                    </span>
                  )}
                  {!per && keptAs(line.unitCost) && (
                    <span className="itemlines__hint">Will be saved as {keptAs(line.unitCost)}</span>
                  )}
                  {lastCosts &&
                    lastCost(
                      lastCosts,
                      line,
                      locked ? undefined : (unitCost) => update(line.key, { unitCost, costAuto: true }),
                      per,
                    )}
                </div>
              );
            })()}

            {showCost && (
              <div className="itemlines__cell">
                <span className="itemlines__label">Line total<span className="sr-only"> for item {index + 1}</span></span>
                <span className="itemlines__static">{total === null ? '—' : money(String(total))}</span>
              </div>
            )}

            {!locked && (
              <div className="itemlines__cell">
                {canRemove(line) && (
                  <button
                    ref={buttonRef(line.key)}
                    type="button"
                    className="btn btn--ghost itemlines__remove"
                    aria-label={`Remove item ${index + 1}`}
                    title="Remove this line"
                    onClick={() => remove(line.key)}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}

            {delivery && (line.skuId || line.qty.trim() || line.lineId) && (
              <DeliveryStrip
                lines={lines}
                index={index}
                options={delivery}
                showBin={deliveryBins}
                offer={offer}
                onChange={(patch, field, oldValue, offerNow) => changeDelivery(index, patch, field, oldValue, offerNow)}
                onApply={applyOffer}
                onDismiss={() => setOffer(null)}
              />
            )}
          </div>
        );
      })}

      {delivery && (
        <datalist id={VEHICLE_LIST_ID}>
          {offeredVehicles(lines, vehicles).map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      )}

      <div className="itemlines__foot">
        {!locked ? (
          <button
            type="button"
            className="btn"
            onClick={addLine}
          >
            + Add item
          </button>
        ) : (
          <span />
        )}
        {totalsBelow && <ItemTotals lines={lines} showCost={showCost} bySupplier={delivery !== undefined} />}
      </div>
    </div>
  );
}

/**
 * What the lines add up to: how many there are, and with costs shown, the
 * total — split by supplier when a stock-in has several, so it is plain what
 * is owed to whom.
 */
export function ItemTotals({
  lines,
  showCost,
  bySupplier = false,
  summary,
}: {
  lines: readonly ReceiptLine[];
  showCost: boolean;
  bySupplier?: boolean;
  /** Replaces the plain "3 lines" count, e.g. "3 items · 2 suppliers". */
  summary?: string | undefined;
}) {
  // The sum of the rounded lines, as on the posted receipt — not the rounded
  // sum, which can drift a paisa from what the lines above it add up to.
  const total = roundMoney(lines.reduce((sum, l) => sum + (lineTotal(l) ?? 0), 0));
  const filled = lines.filter((l) => l.skuId && l.qty).length;
  const groups = bySupplier ? supplierGroups(lines) : [];
  return (
    <div className="itemlines__totals" aria-live="polite">
      <span className="muted">{summary ?? `${filled} ${filled === 1 ? 'line' : 'lines'}`}</span>
      {showCost &&
        groups.length > 1 &&
        groups.map((g) => (
          <span key={g.supplierId || 'none'} className="muted">
            {g.supplierLabel || newSupplierName(g.supplierId) || 'No supplier yet'}
            <strong>{money(String(g.total ?? 0))}</strong>
          </span>
        ))}
      {showCost && (
        <span>
          Total<strong>{money(String(total))}</strong>
        </span>
      )}
    </div>
  );
}
